import { IModelAssignmentResponse } from '@kcftech/prometheustypescriptapiclient';
import { NoValue } from 'components/no_value/NoValue';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { Tag } from 'components/tag/Tag';
import { usePopup } from 'custom_hooks/usePopup';
import styled from 'styled-components';

export const ModelParametersGridContainer = styled.div`
    display: grid;
    grid-template-columns: 5fr 1.5fr 2fr 0.5fr;
    grid-template-areas: 'parameter-name parameter-type parameter-value parameter-required';
    align-items: start;
    padding: 0.25rem;
    column-gap: 1rem;
`;

const ModelParametersGridText = styled.span`
    color: var(--text-primary);
`;

const ModelParameterName = styled(ModelParametersGridText)`
    grid-area: parameter-name;
    width: 90%;
`;

const ModelParameterRequired = styled(ModelParametersGridText)`
    grid-area: parameter-required;
`;

const ModelParameterType = styled(ModelParametersGridText)`
    grid-area: parameter-type;
`;

const ModelParameterValue = styled(ModelParametersGridText)`
    grid-area: parameter-value;
    width: 90%;
`;

const ModelParametersList = styled.div`
    overflow-y: auto;
    max-height: 30rem;
`;

const ModelParametersHeader = styled(ModelParametersGridContainer)`
    font-weight: 500;
    font-size: 1.5rem;
    padding-bottom: 1.5rem;
`;

const ModelParametersContainer = styled.div`
    padding: 3rem;
    background: var(--background-paper-elevation-8);
`;

export type AssignedModelParametersProps = Readonly<{
    rowData: IModelAssignmentResponse;
}>;

export function AssignedModelParameters({ rowData }: AssignedModelParametersProps) {
    const { isOpen, open, close, triggerRef, popupRef } = usePopup();

    const assignedModelParameters = rowData.Properties;
    const assignedModelParametersLength = rowData.Properties.length;

    return (
        <div>
            {assignedModelParametersLength > 0 ? (
                <Tag onSelect={open} selected={isOpen}>
                    <span ref={triggerRef}>
                        {assignedModelParametersLength}{' '}
                        {assignedModelParametersLength === 1 ? 'Parameter' : 'Parameters'}
                    </span>
                </Tag>
            ) : (
                <Tag>No Parameters</Tag>
            )}
            {isOpen && assignedModelParametersLength > 0 && (
                <StatelessPopup
                    noPadding
                    ref={popupRef}
                    anchor={triggerRef.current}
                    anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
                    popupAlign={{ horizontal: 'right', vertical: 'top' }}
                    popupClassName="modal-popup"
                    onClose={close}
                >
                    <ModelParametersContainer>
                        <ModelParametersHeader>
                            <ModelParameterName>Name</ModelParameterName>
                            <ModelParameterType>Type</ModelParameterType>
                            <ModelParameterValue>Value</ModelParameterValue>
                            <ModelParameterRequired>Required</ModelParameterRequired>
                        </ModelParametersHeader>
                        <ModelParametersList>
                            {assignedModelParameters.map((modelParameter) => (
                                <ModelParametersGridContainer
                                    key={modelParameter.PropertyId}
                                >
                                    <ModelParameterName
                                        title={modelParameter.PropertyName}
                                        className="ellipsis-1-line"
                                    >
                                        {modelParameter.PropertyName}
                                    </ModelParameterName>
                                    <ModelParameterType
                                        title={modelParameter.PropertyValueType}
                                        className="ellipsis-1-line"
                                    >
                                        {modelParameter.PropertyValueType}
                                    </ModelParameterType>
                                    <ModelParameterValue
                                        title={modelParameter.PropertyValue}
                                        className="ellipsis-1-line"
                                    >
                                        {modelParameter.PropertyValue ?? <NoValue />}
                                    </ModelParameterValue>
                                    <ModelParameterRequired className="ellipsis-1-line">
                                        {modelParameter.PropertyRequired ? 'Yes' : 'No'}
                                    </ModelParameterRequired>
                                </ModelParametersGridContainer>
                            ))}
                        </ModelParametersList>
                    </ModelParametersContainer>
                </StatelessPopup>
            )}
        </div>
    );
}
